import { useContext, useEffect, useCallback, useRef } from 'react';
import {
  useStatusConsentProxy,
  useProxyRead as useCoreProxyRead,
  useVerifyProxy,
  ProxyAuthorizationRequestStatus,
  ProxyStatusResponseWrapper,
  ProxyAuthorizationResponseWrapper,
  VerifyProxyRequest,
  VerifyProxyResponseWrapper,
} from '@sentara-health/sentara-api-hooks-core';
import { GlobalContext } from 'context';
import { getDecryptedValue } from '../utils/LocalStorageUtils';
import { UserDetails } from '../utils/constants/Constants';

export type ProxyAlertDetails = {
  type: 'Pending' | 'Approved' | 'Denied' | 'Terminated' | 'Expired';
  name: string;
  status: ProxyAuthorizationRequestStatus;
};

export type ProxyPopupState = {
  showApprovalPopup: boolean; // For Pending - Accept/Deny
  showApprovedPopup: boolean; // For Approved - Success message
  showDeniedPopup: boolean; // For asking "are you sure you want to deny?"
  showDeniedConfirmation: boolean; // For showing "Request has been denied" final confirmation
  showExpiredBanner: boolean; // For Expired - Access was expired--we should show red banner-1 here{removed}
  showTerminatedBanner: boolean; // For Terminated - Access has removed - we should show red banner-2 here{removed during viewing}
  showInvitationPopup: boolean; // For showing "Send New Invitation" after denial
  alertDetails: ProxyAlertDetails | null;
};

export type UseProxyStatusProps = {
  proxyBannerDismissed?: boolean;
  onStatusChange?: (details: ProxyAlertDetails | null) => void;
};

/**
 * Helper function to create initial proxy popup state
 */
export const createInitialProxyPopupState = (): ProxyPopupState => ({
  showApprovalPopup: false,
  showApprovedPopup: false,
  showDeniedPopup: false,
  showDeniedConfirmation: false,
  showTerminatedBanner: false,
  showExpiredBanner: false,
  showInvitationPopup: false,
  alertDetails: null,
});

/**
 * Helper function to map ProxyAlertDetails to ProxyPopupState
 */
export const mapAlertDetailsToPopupState = (
  details: ProxyAlertDetails | null
): ProxyPopupState => {
  if (!details) {
    return createInitialProxyPopupState();
  }

  const state = createInitialProxyPopupState();
  state.alertDetails = details;

  switch (details.type) {
    case 'Pending':
      state.showApprovalPopup = true;
      break;
    case 'Approved':
      state.showApprovedPopup = true;
      break;
    case 'Denied':
      state.showInvitationPopup = true; // DR receives notification that request was denied
      break;
    case 'Terminated':
      state.showTerminatedBanner = true;
      break;
    case 'Expired':
      state.showExpiredBanner = true;
      break;
  }

  return state;
};

export const isActiveDesignation = (
  designationStatus?: string,
  status?: string
): boolean =>
  (designationStatus ?? '').toLowerCase() === 'designate' &&
  ['approved', 'pending'].includes((status ?? '').toLowerCase());

/**
 * Returns true when a proxy-read response contains at least one active
 * designation (approved or pending) for the current guest.
 */
export const hasActiveDesignation = (
  readResponse:
    | {
        status?: number;
        data?: { proxies?: Array<Record<string, unknown>> };
      }
    | null
    | undefined
): boolean => {
  if (readResponse?.status !== 200) {
    return false;
  }

  const proxies = readResponse?.data?.proxies;
  if (!Array.isArray(proxies) || proxies.length === 0) {
    return false;
  }

  return proxies.some((proxy) => {
    const consents = (proxy?.consents ?? proxy?.Consents) as
      | Array<Record<string, unknown>>
      | undefined;

    if (!Array.isArray(consents) || consents.length === 0) {
      return false;
    }

    return consents.some((consent) => {
      const designationStatus =
        typeof consent?.designationStatus === 'string'
          ? consent.designationStatus
          : undefined;
      const consentStatus =
        consent?.proxyAuthorizationRequestStatus ??
        consent?.ProxyAuthorizationRequestStatus;
      const status =
        typeof consentStatus === 'string'
          ? consentStatus
          : undefined;

      return isActiveDesignation(designationStatus, status);
    });
  });
};

/**
 * Helper function to map proxy status to alert details
 */
const mapStatusToAlertDetails = (
  status: ProxyAuthorizationRequestStatus | undefined,
  fullName: string = 'The designated representative'
): ProxyAlertDetails | null => {
  if (!status) return null;

  const statusMap: Record<ProxyAuthorizationRequestStatus, ProxyAlertDetails['type']> = {
    Terminated: 'Terminated',
    Denied: 'Denied',
    Expired: 'Expired',
    Approved: 'Approved',
    Pending: 'Pending',
  };

  const type = statusMap[status];
  if (!type) return null;

  return {
    type,
    name: fullName,
    status,
  };
};

/**
 * Process proxies and return the first alert details found
 */
const processProxies = (
  proxies: any[],
  fullName: string
): ProxyAlertDetails | null => {
  for (const proxy of proxies) {
    const consents = proxy.consents || proxy.Consents || [];

    for (const consent of consents) {
      const alertDetails = mapStatusToAlertDetails(
        consent.proxyAuthorizationRequestStatus,
        fullName
      );

      if (alertDetails) {
        return alertDetails;
      }
    }
  }

  return null;
};

type UserPersonalData = {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  emailAddress: string;
};

type ProfileEmailValue =
  | string
  | { value?: string }
  | Array<{ system?: string; value?: string }>
  | null
  | undefined;

// Resolves an email from a string, a { value } object, or a telecom-style array.
const extractEmailAddress = (value: ProfileEmailValue): string => {
  if (typeof value === 'string') {
    return value.trim();
  }

  if (Array.isArray(value)) {
    const emailEntry = value.find(
      (item) =>
        typeof item?.value === 'string' &&
        item.value.trim() &&
        (item.system === undefined || item.system.toLowerCase() === 'email')
    );
    return emailEntry?.value?.trim() ?? '';
  }

  if (value && typeof value === 'object' && typeof value.value === 'string') {
    return value.value.trim();
  }

  return '';
};

const normalizeDateOfBirth = (rawDateOfBirth: unknown): string => {
  if (typeof rawDateOfBirth !== 'string') {
    return '';
  }

  const trimmedDate = rawDateOfBirth.trim();
  if (!trimmedDate) {
    return '';
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedDate)) {
    return trimmedDate;
  }

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmedDate)) {
    const [month, day, year] = trimmedDate.split('/');
    return `${year}-${month}-${day}`;
  }

  const parsedDate = new Date(trimmedDate);
  if (Number.isNaN(parsedDate.getTime())) {
    return '';
  }

  return parsedDate.toISOString().slice(0, 10);
};

const getUserPersonalData = async (
  profileEmail: ProfileEmailValue = '',
  contextTelecom: ProfileEmailValue = ''
): Promise<UserPersonalData | null> => {
  try {
    const cachedLoginResponseRaw = await getDecryptedValue(UserDetails.guest);
    const parsedLoginData =
      typeof cachedLoginResponseRaw === 'string'
        ? JSON.parse(cachedLoginResponseRaw || '{}')
        : cachedLoginResponseRaw ?? {};

    const userDetails = parsedLoginData?.user ?? {};

    const firstName = (userDetails?.firstName ?? '').trim();
    const lastName = (userDetails?.lastName ?? '').trim();
    const dateOfBirth = normalizeDateOfBirth(
      userDetails?.dob ?? userDetails?.dateOfBirth
    );

    // Email priority: loaded profile -> cached user -> cached/context telecom.
    const emailAddress =
      extractEmailAddress(profileEmail) ||
      extractEmailAddress(userDetails?.emailAddress) ||
      extractEmailAddress(userDetails?.email) ||
      extractEmailAddress(userDetails?.emailId) ||
      extractEmailAddress(parsedLoginData?.telecom) ||
      extractEmailAddress(contextTelecom);

    if (!firstName || !lastName || !dateOfBirth || !emailAddress) {
      return null;
    }

    return {
      firstName,
      lastName,
      dateOfBirth,
      emailAddress,
    };
  } catch (error) {
    console.error('Error resolving profile data for proxy verification:', error);
    return null;
  }
};

export const useProxyStatus = ({
  proxyBannerDismissed = false,
  onStatusChange,
}: UseProxyStatusProps = {}): {
  checkProxyAccessStatus: () => Promise<boolean>;
  isLoading: boolean;
} => {
  const {
    getFeature,
    proxyAuthorizationLinkData,
    profileState,
    userTelecom,
  } = useContext(GlobalContext);
  const { getProxyConsentStatus } = useStatusConsentProxy();
  const { verifyProxy } = useVerifyProxy();

  // designatorLoginId and correlationId originate from the custom request URL
  // link the member follows to log in, and are persisted in context.
  const designatorLoginId = proxyAuthorizationLinkData?.forgerockUuid ?? '';
  const correlationId = proxyAuthorizationLinkData?.correlationId ?? '';
  const isDesignatedRepresentativeEnabled =
    getFeature?.DesignatedRepresentativeTab ?? false;

  // SDK recreates these each render; keep stable refs so the callback identity never changes.
  const getProxyConsentStatusRef = useRef(getProxyConsentStatus);
  getProxyConsentStatusRef.current = getProxyConsentStatus;
  const verifyProxyRef = useRef(verifyProxy);
  verifyProxyRef.current = verifyProxy;
  const onStatusChangeRef = useRef(onStatusChange);
  onStatusChangeRef.current = onStatusChange;
  // Context profile values are read through refs so the callback identity stays stable.
  const profileEmailRef = useRef(profileState?.email);
  profileEmailRef.current = profileState?.email;
  const userTelecomRef = useRef(userTelecom);
  userTelecomRef.current = userTelecom;

  // Guards against re-calling the status API for the same identifier pair.
  const requestedKeyRef = useRef<string | null>(null);
  // Remembers whether the last verify call succeeded, for routing decisions.
  const verifiedResultRef = useRef(false);

  const checkProxyAccessStatus = useCallback(async (): Promise<boolean> => {
    // Skip if banner was already dismissed in this session
    if (proxyBannerDismissed) {
      return false;
    }

    // Only run when the Designated Representative LD flag is enabled
    if (!isDesignatedRepresentativeEnabled) {
      return false;
    }

    // Skip if the identifiers from the request URL link are unavailable
    if (!designatorLoginId || !correlationId) {
      return false;
    }

    // Only call once per unique identifier pair
    const requestKey = `${designatorLoginId}|${correlationId}`;
    if (requestedKeyRef.current === requestKey) {
      return verifiedResultRef.current;
    }
    requestedKeyRef.current = requestKey;

    try {
      const userPersonalData = await getUserPersonalData(
        profileEmailRef.current,
        userTelecomRef.current
      );
      if (!userPersonalData) {
        requestedKeyRef.current = null;
        return false;
      }

      const verifyRequest: VerifyProxyRequest = {
        identifier: [
          {
            system: 'designatorLoginId',
            value: designatorLoginId,
          },
          {
            system: 'correlationId',
            value: correlationId,
          },
        ],
        firstName: userPersonalData.firstName,
        lastName: userPersonalData.lastName,
        dateOfBirth: userPersonalData.dateOfBirth,
        emailAddress: userPersonalData.emailAddress,
        personaType: 'Caregiver',
      };

      const verifyResponse: VerifyProxyResponseWrapper = await verifyProxyRef.current(verifyRequest);

      // Check for 401 Unauthorized and immediately sign out (use inline check for hook context)
      const { logoutErrorHandler } = require('common');
      if (logoutErrorHandler(verifyResponse?.errorCode)) {
        requestedKeyRef.current = null;
        return false;
      }

      const verifySucceeded =
        verifyResponse?.status === 200 &&
        !verifyResponse?.error &&
        !verifyResponse?.errorCode;

      if (!verifySucceeded) {
        requestedKeyRef.current = null;
        return false;
      }

      const response: ProxyStatusResponseWrapper =
        await getProxyConsentStatusRef.current({
          identifier: [
            {
              system: 'designatorLoginId',
              value: designatorLoginId,
            },
            {
              system: 'correlationId',
              value: correlationId,
            },
          ],
        });

      // The payload may be returned flat or nested under StatusConsentProxyResponse.
      const statusData: any =
        (response?.data as any)?.StatusConsentProxyResponse ?? response?.data;

      if (statusData?.proxies && Array.isArray(statusData.proxies)) {
        const humanName = statusData.humanName;
        const firstName = humanName?.given?.[0] || '';
        const lastName = humanName?.family || '';
        const fullName = `${firstName} ${lastName}`.trim();

        const alertDetails = processProxies(statusData.proxies, fullName);

        onStatusChangeRef.current?.(alertDetails);
      }

      verifiedResultRef.current = true;
      return true;
    } catch (error) {
      console.error('Error checking proxy status:', error);
      // Allow a retry on a later mount if the call failed
      requestedKeyRef.current = null;
      onStatusChangeRef.current?.(null);
      return false;
    }
  }, [
    proxyBannerDismissed,
    isDesignatedRepresentativeEnabled,
    designatorLoginId,
    correlationId,
  ]);

  return {
    checkProxyAccessStatus,
    isLoading: false,
  };
};

export const useProxyStatusEffect = (props: UseProxyStatusProps = {}) => {
  const { checkProxyAccessStatus } = useProxyStatus(props);

  useEffect(() => {
    checkProxyAccessStatus();
  }, [checkProxyAccessStatus]);

  return { checkProxyAccessStatus };
};

// ============================================================
// PROXY READ API - Reads entitlements (all proxies) for the logged-in user
// ============================================================

export const useProxyRead = (): {
  getProxyRead: () => Promise<ProxyAuthorizationResponseWrapper>;
} => {
  const { getProxyEntitlements } = useCoreProxyRead();
  // SDK recreates getProxyEntitlements each render; keep a stable ref so getProxyRead never changes.
  const getProxyEntitlementsRef = useRef(getProxyEntitlements);
  getProxyEntitlementsRef.current = getProxyEntitlements;

  const getProxyRead = useCallback(async (): Promise<ProxyAuthorizationResponseWrapper> => {
    try {
      return await getProxyEntitlementsRef.current();
    } catch (error) {
      return {
        status: 500,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }, []);

  return { getProxyRead };
};
